import { expandLook, type DecorField, type DecorLook } from '@peles/shared';
import ColorField, { type ColorSwatch } from '../common/ColorField';
import { DecorChoice } from '../floor-plan/look/DecorChoice';
import { FlowerColorsField } from '../floor-plan/look/FlowerColorsField';
import { DECOR_FIELD_SPECS, fieldApplies } from './decorFields';

export interface DecorFieldsEditorProps {
  fields: readonly DecorField[];
  look: DecorLook;
  onChange: (next: DecorLook) => void;
  /** What each field is without a choice here — the plan's, the event's, the default. */
  inherited: Record<string, unknown>;
  /** The first pill and the hint under a colour: "Plan’s", "Not set". */
  inheritedLabel: string;
  swatches?: ColorSwatch[];
}

/**
 * The fields of the look named by `fields`, each asked for its own way
 * (`DECOR_FIELD_SPECS`). Leaving a field alone keeps it inherited; every
 * change answers the whole look with that field set or taken off.
 */
export function DecorFieldsEditor({
  fields,
  look,
  onChange,
  inherited,
  inheritedLabel,
  swatches = [],
}: DecorFieldsEditorProps) {
  const values = look as Record<string, unknown>;
  const set = (field: DecorField, value: unknown) => {
    const next = { ...look } as Record<string, unknown>;
    if (value === undefined) delete next[field];
    else next[field] = value;
    onChange(next as DecorLook);
  };
  // A piece chosen at this level decides its fields unless one is set by
  // hand: what a field shows without its own value is the piece's, else what
  // is inherited — so a tinted glass picked here shows its colour field.
  const fromPicks = expandLook(look.picks ? { picks: look.picks } : null) as Record<
    string,
    unknown
  >;
  const below = (field: DecorField) =>
    fromPicks[field] !== undefined ? fromPicks[field] : inherited[field];
  const belowLabel = (field: DecorField) =>
    fromPicks[field] !== undefined ? 'Chosen piece’s' : inheritedLabel;
  const shown = { ...inherited, ...fromPicks, ...values };

  return (
    <div className="space-y-4">
      {fields
        .filter((f) => fieldApplies(f, shown))
        .map((field) => {
          const spec = DECOR_FIELD_SPECS[field];
          const value = values[field];
          if (spec.kind === 'choice' || spec.kind === 'boolean') {
            const options =
              spec.kind === 'choice'
                ? spec.options
                : [
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ];
            const asText = (v: unknown) =>
              spec.kind === 'boolean' ? (v === undefined ? undefined : v ? 'yes' : 'no') : v;
            const inheritedText =
              options.find((o) => o.value === asText(below(field)))?.label ?? '—';
            return (
              <DecorChoice<string>
                key={field}
                label={spec.label}
                options={options}
                value={asText(value) as string | undefined}
                onChange={(v) =>
                  set(
                    field,
                    spec.kind === 'boolean' ? (v === undefined ? undefined : v === 'yes') : v,
                  )
                }
                inheritedLabel={`${belowLabel(field)}: ${inheritedText}`}
              />
            );
          }
          if (spec.kind === 'colors') {
            return (
              <FlowerColorsField
                key={field}
                label={spec.label}
                value={value as string[] | undefined}
                inherited={(below(field) as string[] | undefined) ?? []}
                inheritedLabel={belowLabel(field)}
                onChange={(v) => set(field, v)}
                swatches={swatches}
              />
            );
          }
          return (
            <ColorField
              key={field}
              label={spec.label}
              value={value as string | undefined}
              onChange={(v) => set(field, v)}
              inheritedValue={(below(field) as string | null | undefined) ?? null}
              inheritedLabel={belowLabel(field)}
              swatches={swatches}
            />
          );
        })}
    </div>
  );
}
